import { useAuth, useSession, useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import { useEffect, useMemo, useState } from 'react'
import { TENANT_BACKEND_API_KEY_GATE } from '../utility/tenantBackendApiKey'
import {
  createClerkSupabaseClientCommandCenter,
  createClerkSupabaseClientLandingZone,
  extractPublicMetadataFromContxtUser,
  getUserPublicMetadataClerk,
  isUserNotFoundMetadataError,
} from '../utility/utility'

export const STOP_FRONTEND_JWT_METADATA_GATE = 'fe-1403-stop-frontend-jwt-metadata'

const EMPTY_PUBLIC_METADATA = {}

/**
 * Resolves Clerk user public metadata from the Contxt API when the feature gate is on,
 * otherwise falls back to JWT-derived Clerk user publicMetadata.
 */
export function useUserPublicMetadata() {
  const { user } = useUser()
  const { getToken } = useAuth()
  const { session } = useSession()
  const { client, isLoading: isStatsigLoading } = useStatsigClient()
  const isStatsigReady = !isStatsigLoading
  const useApiMetadata =
    isStatsigReady && Boolean(client?.checkGate?.(STOP_FRONTEND_JWT_METADATA_GATE))
  const useTenantBackendApiKey =
    isStatsigReady && Boolean(client?.checkGate?.(TENANT_BACKEND_API_KEY_GATE))
  const landingZoneClient = useMemo(
    () => (useTenantBackendApiKey ? createClerkSupabaseClientLandingZone(session) : null),
    [session, useTenantBackendApiKey],
  )
  const commandCenterClient = useMemo(
    () => (useTenantBackendApiKey ? createClerkSupabaseClientCommandCenter(session) : null),
    [session, useTenantBackendApiKey],
  )

  const [apiPublicMetadata, setApiPublicMetadata] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isUserNotFound, setIsUserNotFound] = useState(false)

  useEffect(() => {
    if (!useApiMetadata || !user?.id) {
      setApiPublicMetadata(null)
      setIsLoading(false)
      setError(null)
      setIsUserNotFound(false)
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    setIsUserNotFound(false)

    const fetchMetadata = async () => {
      try {
        const clerkToken = await getToken()
        const userResponse = await getUserPublicMetadataClerk(clerkToken, user.id, {
          landingZoneClient,
          commandCenterClient,
          useTenantBackendApiKey,
        })
        if (!cancelled) {
          setApiPublicMetadata(extractPublicMetadataFromContxtUser(userResponse))
          setIsUserNotFound(false)
        }
      } catch (fetchError) {
        if (!cancelled) {
          const userNotFound = isUserNotFoundMetadataError(fetchError)
          setIsUserNotFound(userNotFound)
          setError(userNotFound ? null : fetchError)
          setApiPublicMetadata(null)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void fetchMetadata()

    return () => {
      cancelled = true
    }
  }, [
    useApiMetadata,
    user?.id,
    getToken,
    landingZoneClient,
    commandCenterClient,
    useTenantBackendApiKey,
  ])

  const publicMetadata = useMemo(() => {
    if (useApiMetadata) {
      return apiPublicMetadata ?? EMPTY_PUBLIC_METADATA
    }

    return user?.publicMetadata ?? EMPTY_PUBLIC_METADATA
  }, [useApiMetadata, apiPublicMetadata, user?.publicMetadata])

  const publicMetadataErrorMessage = error?.message ?? null

  const isAwaitingApiMetadata =
    useApiMetadata &&
    Boolean(user?.id) &&
    apiPublicMetadata === null &&
    error === null &&
    !isUserNotFound

  const isPublicMetadataLoading =
    !isStatsigReady || (useApiMetadata && (isLoading || isAwaitingApiMetadata))

  return {
    publicMetadata,
    isLoading: isPublicMetadataLoading,
    error: useApiMetadata ? error : null,
    publicMetadataErrorMessage: useApiMetadata ? publicMetadataErrorMessage : null,
    isUserNotFound: useApiMetadata ? isUserNotFound : false,
    useApiMetadata,
  }
}
